// pending-0.1.19 bump for RAO PersonSearchBar port
/**
 * PersonSearchBar — RAO module wrapper over the `GlobalCustomerSearch`
 * primitive. Adds RAO-specific behavior that should NOT live in the shared
 * primitive:
 *
 *   - CustInq hydration (full CIF record) when an officer picks a row, before
 *     handing a hydrated `RaoPerson` back to the caller.
 *   - "+ New Person" button dispatches the blank-person flow in PeopleStep.
 *
 * This preserves the legacy SPFx `PersonSearchBar` contract:
 *   `(onSelectExisting: (Person) => void, onAddNew: () => void)`
 * while reusing the single audited `GlobalCustomerSearch` primitive for the
 * actual search UX, kill-list filter, and card grid.
 *
 * NOTE: this wrapper does not introduce a second network search path — it
 * defers 100% of the search fan-out + dedupe + kill-list to the primitive.
 */
import { useCallback, useState } from 'react';
import { GlobalCustomerSearch, type CustomerSearchRow } from '@/components/primitives';
import { useCustomerPick } from '../../hooks/useCustomerPick';
import type { RaoPerson, RaoPersonRole } from '../../types';

export interface PersonSearchBarProps {
  /** Cart id - the remembered search query is scoped to it. */
  persistScope?: string;
  /** CIFs already in the cart — shown as "In Cart" + unclickable. */
  cartCifIds?: ReadonlyArray<string>;
  /** Role assigned to a newly selected / added person. Defaults to 'primary'. */
  pendingRole?: RaoPersonRole;
  /** Called once the picked CIF has been hydrated with CustInq data. */
  onSelectExisting: (person: RaoPerson) => void;
  /** Called when officer clicks "+ New Person". Parent owns the blank-person modal. */
  onAddNew: () => void;
}

/** RAO-scoped search bar. Thin wrapper over GlobalCustomerSearch + useCustomerPick. */
export function PersonSearchBar({
  cartCifIds,
  pendingRole = 'primary',
  persistScope,
  onSelectExisting,
  onAddNew,
}: PersonSearchBarProps): JSX.Element {
  const { pick } = useCustomerPick();
  const [hydratingCif, setHydratingCif] = useState<string | null>(null);

  /**
   * When a row is clicked in the primitive, hydrate it (CustInq +
   * useInquiryHydration) into a fully-populated RaoPerson via the shared
   * `useCustomerPick` hook, then hand it to the parent's review-add flow.
   * The hydration path is shared with PeopleStep's no-dup-CIF guard so the
   * two callers can never drift (the 2026-04-28 dropped-fields bug).
   */
  const onSelect = useCallback(
    async (row: CustomerSearchRow): Promise<void> => {
      setHydratingCif(row.cifId);
      try {
        const draft = await pick(row, pendingRole);
        onSelectExisting(draft);
      } finally {
        setHydratingCif(null);
      }
    },
    [pick, onSelectExisting, pendingRole],
  );

  // hydratingCif reserved for future inline spinner (parent currently shows
  // a "Loading full CIF record…" toast). Kept in state so future versions
  // can surface per-row loading without another re-render pass.
  void hydratingCif;

  return (
    <GlobalCustomerSearch
      {...(persistScope ? { persistScope } : {})}
      onSelect={(row) => { void onSelect(row); }}
      onAddNew={onAddNew}
      {...(cartCifIds ? { cartCifIds } : {})}
    />
  );
}
